import { useMemo } from "react";
import type { TimeReportsV2Group } from "services/api/company/timeReports.service";
import { GroupDivider } from "web-apps/company/components/common/shared/groupDivider.component";
import { TimeReportsGroupByOption } from "../../../toolbar/timeReportsToolbar.component";
import { PersonDetailSelection } from "../models/personView.types";
import { PersonRow } from "./row/personRow.component";

interface PersonRowsListProps {
  rows: TimeReportsV2Group[];
  groupByOption: TimeReportsGroupByOption;
  queryScopeKey: string;
  isAllRowsExpanded: boolean;
  deviationToleranceMinutes: number;
  includeFlexTimeDeviations: boolean;
  selectedDetailIdSet: Set<number>;
  onDownloadRowCsv: (row: TimeReportsV2Group) => void;
  onSelectionChange: (
    selection: PersonDetailSelection[],
    checked: boolean,
  ) => void;
  onDetailClick: (selection: PersonDetailSelection) => void;
  translate: (key: string) => string;
}

const toSelectedDetailIdsByRowId = (
  rows: TimeReportsV2Group[],
  selectedDetailIdSet: Set<number>,
): Map<number, number[]> => {
  return new Map(
    rows.map((row) => [
      row.id,
      row.items
        .filter((item) => selectedDetailIdSet.has(item.id))
        .map((item) => item.id),
    ]),
  );
};

export const PersonRowsList = (props: PersonRowsListProps): JSX.Element => {
  const rows = props.rows;
  const groupByOption = props.groupByOption;
  const queryScopeKey = props.queryScopeKey;
  const selectedDetailIdSet = props.selectedDetailIdSet;
  const translate = props.translate;

  const selectedDetailIdsByRowId = useMemo(
    () => toSelectedDetailIdsByRowId(rows, selectedDetailIdSet),
    [rows, selectedDetailIdSet],
  );

  const rowsWithDividers = useMemo<
    Array<{
      key: string;
      row: TimeReportsV2Group;
      divider: JSX.Element | null;
    }>
  >(() => {
    if (groupByOption !== "team") {
      return rows.map((row) => ({
        key: `${queryScopeKey}|${String(row.id)}`,
        row,
        divider: null,
      }));
    }

    const result: Array<{
      key: string;
      row: TimeReportsV2Group;
      divider: JSX.Element | null;
    }> = [];
    let lastWorkGroupId: number | null = null;

    rows.forEach((row) => {
      const firstItem = row.items[0];

      const workGroupId = firstItem?.workGroupId ?? null;

      const rawUnitName = (firstItem?.companyUnitName ?? "").trim();

      const unitName =
        rawUnitName.length > 0
          ? rawUnitName
          : translate("Schedule.UnknownUnit");

      const rawTeamName = (firstItem?.workGroupName ?? "").trim();

      const teamName =
        rawTeamName.length > 0
          ? rawTeamName
          : translate("Schedule.UnknownTeam");

      const shouldRenderDivider =
        workGroupId !== null && workGroupId !== lastWorkGroupId;

      result.push({
        key: `${queryScopeKey}|${String(row.id)}`,
        row,
        divider: shouldRenderDivider ? (
          <GroupDivider
            key={`divider-${String(workGroupId)}`}
            unitName={unitName}
            teamName={teamName}
          />
        ) : null,
      });
      lastWorkGroupId = workGroupId;
    });

    return result;
  }, [groupByOption, queryScopeKey, rows, translate]);

  return (
    <>
      {rowsWithDividers.map((entry) => (
        <div key={entry.key}>
          {entry.divider}
          <PersonRow
            row={entry.row}
            isExpandedByDefault={props.isAllRowsExpanded}
            deviationToleranceMinutes={props.deviationToleranceMinutes}
            includeFlexTimeDeviations={props.includeFlexTimeDeviations}
            onDownloadClick={props.onDownloadRowCsv}
            selectedDetailIds={selectedDetailIdsByRowId.get(entry.row.id) ?? []}
            onSelectionChange={props.onSelectionChange}
            onDetailClick={props.onDetailClick}
          />
        </div>
      ))}
    </>
  );
};
